import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Special } from '@common/entities';
import { RootState } from 'state/store';

import { validateSpecials } from 'services';
import handleError from 'utils/handleError';

import { useNotifications } from './useNotifications';

type SpecialSelection = Pick<Special, 'groupId'>;

export function hasNonCouponSpecials(
  selectedSpecials: readonly SpecialSelection[],
  promoCodeSpecials: readonly SpecialSelection[],
): boolean {
  const promoCodeGroupIds = new Set(promoCodeSpecials.map(({ groupId }) => String(groupId)));

  return selectedSpecials.some(({ groupId }) => !promoCodeGroupIds.has(String(groupId)));
}

export function useValidateSpecials(): () => Promise<boolean> {
  const { promoCodeSpecials, selectedSpecials } = useSelector(
    (state: RootState) => state.unitSelection,
  );

  return useCallback(
    async function validateSelectedSpecials(): Promise<boolean> {
      if (!hasNonCouponSpecials(selectedSpecials ?? [], promoCodeSpecials ?? [])) {
        return true;
      }

      try {
        const response = await validateSpecials();
        return response?.data?.result?.status === 'success';
      } catch (error) {
        handleError(`Form Viewer - Error validating specials: ${error}`, undefined);
        throw error;
      }
    },
    [promoCodeSpecials, selectedSpecials],
  );
}

export function useRefreshSpecialValidationNotifications(): () => void {
  const { refetchNotifications } = useNotifications({ enabled: false, retry: 0 });

  return useCallback((): void => {
    setTimeout(() => {
      Promise.resolve(refetchNotifications()).catch((error: unknown) => {
        handleError(
          `Form Viewer - Error refreshing special validation notification: ${error}`,
          undefined,
        );
      });
    }, 500);
  }, [refetchNotifications]);
}
